import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import InboxIcon from '@mui/icons-material/Inbox'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

interface ConfirmationAccountDialogProps {
  open: boolean
  onClose: () => void
  userFirstName: string
}

export const ConfirmationAccountDialog = ({
  open,
  onClose,
  userFirstName,
}: ConfirmationAccountDialogProps) => {
  const { t } = useTranslation()
  const [animationPhase, setAnimationPhase] = useState(0)

  useEffect(() => {
    if (open) {
      setAnimationPhase(0)

      const timers: NodeJS.Timeout[] = []

      // Start movement after initial delay
      timers.push(setTimeout(() => setAnimationPhase(1), 500))
      
      // Arrive at email row
      timers.push(setTimeout(() => setAnimationPhase(2), 1500))
      
      // Click
      timers.push(setTimeout(() => setAnimationPhase(3), 2200))
      
      // Reset for loop
      timers.push(setTimeout(() => setAnimationPhase(0), 2800))
      
      // Restart animation loop
      const loopInterval = setInterval(() => {
        setAnimationPhase(0)
        setTimeout(() => setAnimationPhase(1), 500)
        setTimeout(() => setAnimationPhase(2), 1500)
        setTimeout(() => setAnimationPhase(3), 2200)
      }, 3300)

      return () => {
        timers.forEach(clearTimeout)
        clearInterval(loopInterval)
      }
    }
  }, [open])

  const getCursorStyle = () => {
    switch (animationPhase) {
      case 0:
        return { top: '20px', left: '100px', transition: 'none' }
      case 1:
        return { top: '105px', left: '350px', transition: 'all 1s ease-out' }
      case 2:
        return { top: '105px', left: '350px', transition: 'all 0.2s ease' }
      case 3:
        return { top: '105px', left: '350px', transition: 'all 0.1s ease' }
      default:
        return { top: '20px', left: '100px', transition: 'none' }
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          maxWidth: '750px',
          boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Success Header Section */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #c11b1e 0%, #a0130e 100%)',
            color: 'white',
            p: 4,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background decoration */}
          <Box
            sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 150,
              height: 150,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.1)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 100,
              height: 100,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.05)',
            }}
          />
          
          <CheckCircleIcon sx={{ fontSize: 56, mb: 1.5, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }} />
          
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            {t('Account created successfully')}!
          </Typography>
          
          <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400, lineHeight: 1.5 }}>
            {t('Please check your email and click on the confirmation link to activate your account')}
          </Typography>
        </Box>

        {/* Instruction Arrow Section */}
        <Box
          sx={{
            backgroundColor: '#fff8e1',
            borderBottom: '1px solid #ffe082',
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
          }}
        >
          <MailOutlineIcon sx={{ color: '#f57c00', fontSize: 22 }} />
          <Typography sx={{ color: '#e65100', fontWeight: 600, fontSize: 14 }}>
            {t('Open your inbox and click on the email from Layerstock')} 👇
          </Typography>
        </Box>

        {/* Gmail Interface Mock */}
        <Box
          sx={{
            position: 'relative',
            backgroundColor: '#f6f8fc',
            overflow: 'hidden',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          {/* Gmail Top Bar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#f6f8fc',
              borderBottom: '1px solid #e8eaed',
              px: 1.5,
              py: 0.75,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid #dadce0',
                  borderRadius: '4px',
                  px: 0.75,
                  py: 0.25,
                  backgroundColor: 'white',
                }}
              >
                <CheckBoxOutlineBlankIcon sx={{ fontSize: 18, color: '#5f6368' }} />
                <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#5f6368' }} />
              </Box>
              <RefreshIcon sx={{ fontSize: 20, color: '#5f6368' }} />
              <MoreVertIcon sx={{ fontSize: 20, color: '#5f6368' }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 12, color: '#5f6368' }}>1-50 din 13.426</Typography>
              <ChevronLeftIcon sx={{ fontSize: 20, color: '#c4c4c4' }} />
              <ChevronRightIcon sx={{ fontSize: 20, color: '#5f6368' }} />
            </Box>
          </Box>

          {/* Gmail Tabs */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'white',
              borderBottom: '1px solid #e8eaed',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.2,
                borderBottom: '3px solid #d93025',
                color: '#d93025',
              }}
            >
              <InboxIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Principale</Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.2,
                color: '#5f6368',
              }}
            >
              <LocalOfferOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 13 }}>Oferte</Typography>
              <Box
                sx={{
                  backgroundColor: '#188038',
                  color: 'white',
                  fontSize: 11,
                  px: 0.7,
                  py: 0.1,
                  borderRadius: '4px',
                  fontWeight: 500,
                }}
              >
                18 noi
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.2,
                color: '#5f6368',
              }}
            >
              <PeopleOutlineIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 13 }}>Rețele sociale</Typography>
              <Box
                sx={{
                  backgroundColor: '#1a73e8',
                  color: 'white',
                  fontSize: 11,
                  px: 0.7,
                  py: 0.1,
                  borderRadius: '4px',
                  fontWeight: 500,
                }}
              >
                5 noi
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.2,
                color: '#5f6368',
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 13 }}>Actualizări</Typography>
              <Box
                sx={{
                  backgroundColor: '#f9ab00',
                  color: 'white',
                  fontSize: 11,
                  px: 0.7,
                  py: 0.1,
                  borderRadius: '4px',
                  fontWeight: 500,
                }}
              >
                11 noi
              </Box>
            </Box>
          </Box>

          {/* Email Row - Layerstock */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: animationPhase >= 2 ? '#c2dbff' : 'white',
              borderLeft: '4px solid #c11b1e',
              px: 1,
              py: 1,
              cursor: 'pointer',
              transition: 'all 0.25s ease',
              transform: animationPhase === 3 ? 'scale(0.98)' : 'scale(1)',
              boxShadow: animationPhase >= 2 
                ? 'inset 0 0 0 2px rgba(193, 27, 30, 0.3), 0 2px 8px rgba(0,0,0,0.1)' 
                : 'none',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: '70px' }}>
              <CheckBoxOutlineBlankIcon sx={{ fontSize: 18, color: '#5f6368' }} />
              <StarBorderIcon sx={{ fontSize: 18, color: '#5f6368' }} />
            </Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: '#202124',
                minWidth: '100px',
              }}
            >
              Layerstock
            </Typography>
            <Box sx={{ flex: 1, overflow: 'hidden', mr: 1 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  color: '#202124',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Confirm Your Account
                </Box>
                <Box component="span" sx={{ color: '#5f6368' }}>
                  {' '}- LAYERSTOCK Professional Digital Assets Platform Confirm Your Account hello {userFirstName || 'user'}, Thank you for registering on Layerstock! To complete your registration and ac...
                </Box>
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: 12,
                color: '#5f6368',
                minWidth: '45px',
                textAlign: 'right',
              }}
            >
              11:18
            </Typography>
          </Box>

          {/* Second email row (dimmed) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#f8f9fa',
              px: 1,
              py: 1,
              opacity: 0.5,
              borderBottom: '1px solid #f1f3f4',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: '70px' }}>
              <CheckBoxOutlineBlankIcon sx={{ fontSize: 18, color: '#5f6368' }} />
              <StarBorderIcon sx={{ fontSize: 18, color: '#5f6368' }} />
            </Box>
            <Typography sx={{ fontSize: 13, color: '#5f6368', minWidth: '100px' }}>
              Newsletter
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#5f6368' }}>
              Weekly digest - Your weekly summary is ready...
            </Typography>
          </Box>

          {/* Animated Cursor */}
          <Box
            sx={{
              position: 'absolute',
              ...getCursorStyle(),
              zIndex: 1000,
              pointerEvents: 'none',
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              style={{
                filter: 'drop-shadow(2px 2px 3px rgba(0,0,0,0.4))',
                transform: animationPhase === 3 ? 'scale(0.8) rotate(-5deg)' : 'scale(1)',
                transition: 'transform 0.1s ease',
              }}
            >
              <path
                d="M4 0L4 20L9 15L13 24L16 22.5L12 14L20 14L4 0Z"
                fill="white"
                stroke="#333"
                strokeWidth="1.5"
              />
            </svg>
            {/* Click ripple effect */}
            {animationPhase === 3 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(193, 27, 30, 0.3)',
                  animation: 'ripple 0.4s ease-out',
                  '@keyframes ripple': {
                    '0%': { transform: 'scale(0)', opacity: 1 },
                    '100%': { transform: 'scale(2)', opacity: 0 },
                  },
                }}
              />
            )}
          </Box>
        </Box>

        {/* Footer with OK button */}
        <Box
          sx={{
            p: 3,
            backgroundColor: '#fafafa',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: '500px' }}>
            {t("Didn't receive the email? Check your spam folder or request a new confirmation email from the login page.")}
          </Typography>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              backgroundColor: '#c11b1e',
              '&:hover': {
                backgroundColor: '#a0130e',
              },
              px: 5,
              py: 1.25,
              fontSize: '15px',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(193, 27, 30, 0.3)',
            }}
          >
            {t('Got it!')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}